import { ActionIcon, Group, Stack, Text, Textarea } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { memo, useCallback } from 'react'

import type { FAQItem } from '@/types/faq'

interface FAQEntryProps {
  faq: FAQItem
  onUpdate: (id: string, field: 'question' | 'answer', value: string) => void
  onDelete: (id: string) => void
  disabled?: boolean
  showValidationErrors?: boolean
}

export const FAQEntry = memo<FAQEntryProps>(function FAQEntry({
  faq,
  onUpdate,
  onDelete,
  disabled = false,
  showValidationErrors = false,
}) {
  const handleQuestionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(faq.id, 'question', event.target.value)
    },
    [faq.id, onUpdate],
  )

  const handleAnswerChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(faq.id, 'answer', event.target.value)
    },
    [faq.id, onUpdate],
  )

  const handleDelete = useCallback(() => {
    onDelete(faq.id)
  }, [faq.id, onDelete])

  const questionHasError = showValidationErrors && faq.question.trim().length === 0
  const answerHasError = showValidationErrors && faq.answer.trim().length === 0
  const hasError = questionHasError || answerHasError
  const isIncomplete = hasError

  return (
    <Stack
      gap="xs"
      p="md"
      style={{
        border: `1px solid ${hasError ? 'var(--mantine-color-red-4)' : 'var(--mantine-color-gray-3)'}`,
        borderRadius: '8px',
        opacity: disabled ? 0.6 : 1,
        backgroundColor: hasError ? 'var(--mantine-color-red-0)' : undefined,
      }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Textarea
            placeholder="Enter your question..."
            value={faq.question}
            onChange={handleQuestionChange}
            autosize
            minRows={1}
            maxRows={3}
            size="sm"
            disabled={disabled}
            error={questionHasError}
            styles={{
              input: {
                fontWeight: 500,
                borderColor: questionHasError ? 'var(--mantine-color-red-4)' : undefined,
              },
            }}
          />
          <Textarea
            placeholder="Enter the answer..."
            value={faq.answer}
            onChange={handleAnswerChange}
            autosize
            minRows={2}
            maxRows={6}
            size="sm"
            disabled={disabled}
            error={answerHasError}
            styles={{
              input: {
                borderColor: answerHasError ? 'var(--mantine-color-red-4)' : undefined,
              },
            }}
          />
          {/* Show validation message */}
          {isIncomplete && (
            <Text size="xs" c="orange" fw={500}>
              Both question and answer are required to save this FAQ
            </Text>
          )}
        </Stack>

        <ActionIcon variant="subtle" color="red" size="sm" onClick={handleDelete} disabled={disabled}>
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Stack>
  )
})
